"use client";
import useLogIn from "@hooks/useLogIn";
import { commonReplacements, CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { useApplyLocalizedErrorMessages } from "@lib/validators/yupConfig";
import { getLogInValidationSchema } from "@lib/validators/user/logInSchema";
import { Button } from "@ecco/ui/button";
import { Formik, Form } from "formik";
import type { FC } from "react";
import { useRef, useState } from "react";
import { RichText } from "@components/cms/rich-text-renderer/RichText";
import type { Document } from "@contentful/rich-text-types";
import { Box } from "@ecco/ui/box";
import { FormControl } from "@ecco/ui/form-control";
import { FormLabel } from "@ecco/ui/form-label";
import { Field } from "@ecco/ui/field";
import { FormErrorMessage } from "@ecco/ui/form-error";
import PasswordField from "@components/primitives/PasswordField";
import { Input } from "@ecco/ui/input";
import { getPrivacyAttributes } from "@lib/monitoring/privacyAttributes";
import { useLoginFormPersistence } from "./hooks/useLoginFormPersistence";
import { RUM_ACTION_NAME } from "@lib/monitoring/rumActionNames";

interface Props {
    formId: string;
    onSuccess?: () => void;
    onFail?: () => void;
    forgotPasswordText?: Document;
    isModalLogin?: boolean;
    initialEmail?: string;
}

interface LogInFormValues {
    email: string;
    password: string;
}

const LogInForm: FC<Props> = ({
    formId,
    onSuccess = () => {},
    onFail = () => {},
    forgotPasswordText,
    isModalLogin,
    initialEmail,
}) => {
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const formRef = useRef(undefined);
    const { getInitialEmail, saveEmail, clearLogin } = useLoginFormPersistence();
    const onFailLogIn = () => {
        setIsError(true);
        setErrorMessage(headers.errors.emailOrPasswordError);
        onFail();
    };
    const onSuccessLogIn = () => {
        clearLogin();
        onSuccess();
    };
    const { logIn } = useLogIn({ onSuccess: onSuccessLogIn, onFail: onFailLogIn, isModalLogin });
    const { get: getMicrocopy } = useMicrocopy();

    useApplyLocalizedErrorMessages();

    const headers = {
        title: getMicrocopy(CommonCMS.shared, CommonCMS.logInTitle),
        email: getMicrocopy(CommonCMS.shared, CommonCMS.email),
        password: getMicrocopy(CommonCMS.shared, CommonCMS.password),
        logInButton: getMicrocopy(CommonCMS.shared, CommonCMS.button.logIn),
        errors: {
            email: getMicrocopy(CommonCMS.shared, CommonCMS.error.email),
            emailOrPasswordError: getMicrocopy(
                CommonCMS.shared,
                CommonCMS.error.emailOrPasswordError
            ),
            requiredField: getMicrocopy(CommonCMS.shared, CommonCMS.error.requiredFieldError),
            maxCharacters: getMicrocopy(CommonCMS.shared, CommonCMS.error.maxCharacters, {
                replacements: commonReplacements.maxEmailCharacters,
            }),
        },
    };

    const onSubmit = (values, action) => {
        setIsError(false);
        logIn(values, action);
    };

    return (
        <Formik<LogInFormValues>
            validationSchema={getLogInValidationSchema(headers.errors, {
                email: headers.email,
                password: headers.password,
            })}
            initialValues={{
                email: getInitialEmail(initialEmail),
                password: "",
            }}
            innerRef={formRef}
            onSubmit={onSubmit}
        >
            {({ submitForm, errors, isSubmitting, touched, setFieldValue, handleBlur }) => {
                return (
                    <>
                        <Form noValidate={true} id={formId}>
                            <FormControl
                                isRequired
                                isInvalid={(!!errors.email && touched.email) as boolean}
                            >
                                <FormLabel
                                    htmlFor="email"
                                    id="emailLabel"
                                    fontSize={["mobileBodyTextSmall", "desktopBodyTextSmall"]}
                                >
                                    {headers.email}
                                </FormLabel>
                                <Field
                                    as={Input}
                                    id="email"
                                    name="email"
                                    type="email"
                                    aria-labelledby="emailLabel"
                                    onBlur={(e) => {
                                        const trimmedEmail = e.target.value.trim();
                                        // Adding an empty space at the end will trigger the setter
                                        // Otherwise the value is considered to be unchanged.
                                        setFieldValue("email", trimmedEmail + " ");
                                        saveEmail(trimmedEmail);
                                        handleBlur(e);
                                    }}
                                />
                                <FormErrorMessage>{errors.email}</FormErrorMessage>
                            </FormControl>
                            <FormControl
                                isRequired
                                {...getPrivacyAttributes()}
                                isInvalid={(!!errors.password && touched.password) as boolean}
                            >
                                <FormLabel
                                    htmlFor="password"
                                    id="passwordLabel"
                                    fontSize={["mobileBodyTextSmall", "desktopBodyTextSmall"]}
                                >
                                    {headers.password}
                                </FormLabel>
                                <PasswordField id="password" ariaLabelledBy="passwordLabel" />
                                <FormErrorMessage>{errors.password}</FormErrorMessage>
                            </FormControl>
                            {isError && (
                                <FormControl isInvalid={isError} my={4}>
                                    <FormErrorMessage>{errorMessage}</FormErrorMessage>
                                </FormControl>
                            )}
                        </Form>
                        {forgotPasswordText?.content && (
                            <Box
                                my={6}
                                textAlign={"right"}
                                sx={{
                                    "& button": {
                                        textDecor: "underline",
                                    },
                                }}
                            >
                                <RichText data={forgotPasswordText} />
                            </Box>
                        )}
                        <Button
                            w={"100%"}
                            type={"submit"}
                            form={formId}
                            isLoading={isSubmitting}
                            onClick={submitForm}
                            data-testid="logInButton"
                            data-dd-action-name={RUM_ACTION_NAME.LOG_IN}
                        >
                            {headers.logInButton}
                        </Button>
                    </>
                );
            }}
        </Formik>
    );
};

export default LogInForm;
